<template>
  <SplitViewTemplate
    :screenConfig="data"
    :backButtonRoute="'/settings/intake'"
    :dataModel="formModel"
    :formConfig="setFormConfig"
    :setLoading="setLoading"
    :validationSummary="validationSummary"
    :ref="'huller'"
    :defaultUserSettings="false"
  />
</template>
<script>
import { FeatureStorage } from "@/_services/storage";
import SplitViewTemplate from "../../../templates/split-view";
import { SupplierService } from "../../../_services/api/suppliers/supplier.service";
import RouteMixin from "@/_mixins/route.mixin";

// Sample Taken is a fixed 3-value list, hardcoded per depotdocket.htm's own
// legacy <select> (BR-04, technical-design.md §6/§7 - flagged as known
// technical debt, not resolved by this feature).
const sampleTakenOptions = ["Pre Weighing", "Pre Sizing", "Post Sizing"];

export default {
  mixins: [RouteMixin],
  components: {
    SplitViewTemplate,
  },
  data() {
    return {
      setLoading: false,
      validationSummary: [],
      formModel: {
        SupplierCode: null,
        SupplierName: null,
        // Bug 77351: Review/Edit's read-only textbox binds here instead of
        // the raw SupplierCode, matching intake-variety-size.vue's own
        // CodeName precedent for a locked-after-create coded-lookup field.
        // Populated in recordPostProcess below.
        SupplierCodeName: null,
        SupplierTypeCode: null,
        SupplierTypeDescription: null,
        SampleTaken: null,
        IsSized: null,
        Inactive: false,
        ChangedBy: "",
        CreatedBy: "",
        DateChanged: "",
        DateCreated: "",
      },
      data: {
        title: "Huller",
        pageRoute: "/huller",
        saveEndPoint: "AlmondcoHullerDetail",
        updateEndPoint: "AlmondcoHullerDetail",
        // RecordId is the model's actual EF/OData key (#76529) - no
        // Bin-Comment-style composite-key complication here (BR-01's real
        // physical PK, SuppCode, is immutable and never the routing key).
        updateKeyField: "RecordId",
        updateKeyFieldType: "Guid",
        recordEndPoint: "AlmondcoHullerDetail",
        exportFileName: "Huller List",
        recordPostProcess: this.recordPostProcess,
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "AlmondcoHullerDetail",
              filter: "",
              serverPaging: true,
              allowGrouping: false,
              sort: [{ selector: "SupplierName", desc: false }],
            },
            rowSelect: {
              view: this,
              page: "huller",
              mode: "review",
              idProperty: "RecordId",
              idMode: "Guid",
              rowSelectCallBack: () => {
                this.beforeInit();
              },
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: "/huller/create",
                clickAction: () => {
                  this.goToCreateHuller();
                },
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "AlmondcoHuller",
                  "create",
                ),
              },
            ],
          },
          columns: [
            {
              screenField: "Supplier",
              gridDataField: "SupplierName",
              label: "Supplier",
              allowHiding: false,
              sortIndex: 0,
              sortOrder: "asc",
              cellTemplate: "Record",
              cellTemplateRecord: {
                primary: [
                  {
                    dataField: "SupplierName",
                  },
                ],
                secondary: [
                  {
                    dataField: "SupplierCode",
                  },
                ],
              },
              exportFields: [
                {
                  dataField: "SupplierCode",
                  label: "Supplier Code",
                },
                {
                  dataField: "SupplierName",
                  label: "Supplier Name",
                },
              ],
            },
            {
              // Coded-lookup display convention (description primary, code
              // secondary) - technical-design.md §6.
              screenField: "SupplierType",
              gridDataField: "SupplierTypeDescription",
              label: "Supplier Type",
              cellTemplate: "Record",
              cellTemplateRecord: {
                primary: [
                  {
                    dataField: "SupplierTypeDescription",
                  },
                ],
                secondary: [
                  {
                    dataField: "SupplierTypeCode",
                  },
                ],
              },
              width: 180,
            },
            {
              screenField: "SampleTaken",
              dataField: "SampleTaken",
              label: "Sample Taken",
              width: 120,
            },
            {
              // No sibling screen in this codebase renders a Y/N flag as an
              // icon glyph - plain "Yes"/"No" text is the consistent choice
              // (technical-design.md §6 leaves the exact glyph as Build's
              // choice).
              screenField: "IsSized",
              dataField: "IsSized",
              label: "Is Sized?",
              customizeText: (cellInfo) =>
                cellInfo.value === "Y" ? "Yes" : "No",
              width: 75,
            },
            {
              screenField: "Status",
              width: "auto",
              filterIndex: 0,
            },
          ],
          valueChangedEventListners: [],
        },
      },
      updatedDefaultRecordId: undefined,
      recordEditable: true,
    };
  },
  computed: {
    setFormConfig() {
      const form = {
        headerToolbar: {
          closeSplit: true,
          save: this.setSaveOption,
          audit: true,
          auditItems: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FormattedDateCreated",
                  label: { text: "Created By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
                {
                  dataField: "FormattedDateChanged",
                  label: { text: "Changed By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
              ],
            },
          ],
          toolbarFormItems: [
            {
              screenField: "Inactive",
              editorType: "dxSwitch",
              editorOptions: {
                readOnly: false,
              },
              cssClass: "toolbar-customer-label",
            },
          ],
          toolbarFormLocation: "after",
          editCallBack: () => {
            this.beforeInit();
          },
        },
        formItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                // dxSelectBox (create) / dxTextBox display-only (review,
                // edit) - technical-design.md §6. SuppCode is immutable
                // after create (BR-01), so only Create ever needs the
                // picker. onValueChanged (#76557) cascades Supplier
                // Type/Description from the picked Supplier, client-side -
                // see onSupplierPicked.
                // Bug 77351 fix: Review/Edit's dxTextBox has no
                // displayExpr-equivalent formatting hook (that's a
                // dxSelectBox-only mechanism, used below only in the
                // create-mode branch), so it must bind to a separate
                // pre-formatted "Code - Description" field
                // (SupplierCodeName, set in recordPostProcess) rather than
                // the raw SupplierCode - otherwise the Supplier's
                // description never renders once the field stops being a
                // picker. Matches intake-variety-size.vue's identical
                // Code/CodeName split for the same locked-after-create
                // coded-lookup shape.
                dataField:
                  this.baseView.mode === "create"
                    ? "SupplierCode"
                    : "SupplierCodeName",
                editorType:
                  this.baseView.mode === "create" ? "dxSelectBox" : "dxTextBox",
                editorOptions:
                  this.baseView.mode === "create"
                    ? {
                        tabIndex: 1,
                        dataSource: SupplierService.dataSourceConfig(
                          "api/Supplier",
                          {
                            select: ["Code", "Name", "SupplierTypeCode"],
                            expand: "SupplierType($select=Description)",
                            filter: [["Inactive", "=", "N"]],
                            sort: [{ selector: "Name", asc: true }],
                          },
                        ),
                        valueExpr: "Code",
                        displayExpr: (item) =>
                          item ? item.Code + " - " + item.Name : "",
                        searchEnabled: true,
                        searchExpr: ["Code", "Name"],
                        searchMode: "contains",
                        showClearButton: true,
                        onValueChanged: this.onSupplierPicked,
                      }
                    : {
                        tabIndex: 1,
                        disabled: true,
                      },
                required: true,
                isPrimaryKeyField: true,
                label: { text: "Supplier" },
                visible: true,
                allowUpdate: this.baseView.mode === "create",
              },
              {
                // Always read-only regardless of mode - technical-design.md
                // §6. Informational only (BR-03), sourced from the fetched
                // record's own SupplierTypeCode (#76532's view join).
                dataField: "SupplierTypeCode",
                editorType: "dxTextBox",
                editorOptions: {
                  tabIndex: 2,
                  disabled: true,
                },
                label: { text: "Supplier Type" },
                visible: true,
                allowUpdate: false,
              },
              {
                dataField: "SupplierTypeDescription",
                editorType: "dxTextBox",
                editorOptions: {
                  tabIndex: 3,
                  disabled: true,
                },
                label: { text: "Supplier Type Description" },
                visible: true,
                allowUpdate: false,
              },
              {
                dataField: "SampleTaken",
                editorType: "dxSelectBox",
                editorOptions: {
                  tabIndex: 4,
                  dataSource: sampleTakenOptions,
                },
                required: true,
                label: { text: "Sample Taken" },
                visible: true,
                allowUpdate: this.recordEditable,
              },
              {
                // Raw "Y"/"N" string, not the generic boolean-typed Inactive
                // screenField - needs the same onInitialized sync
                // sample-code.vue's ForeignMaterial/Fruit/IsGrade fields use,
                // otherwise Review mode would show a checked box for "N" too
                // (both are truthy strings).
                dataField: "IsSized",
                editorType: "dxCheckBox",
                editorOptions: {
                  tabIndex: 5,
                  onInitialized: (e) => {
                    e.component.beginUpdate();
                    e.component.option("value", this.formModel.IsSized == "Y");
                    e.component.endUpdate();
                  },
                },
                label: { text: "Is Sized?" },
                visible: true,
                allowUpdate: this.recordEditable,
              },
            ],
          },
        ],
        overWritePayload: this.overWritePayload,
      };

      return form;
    },
    setSaveOption() {
      return FeatureStorage.checkPermission(
        this.$route.meta.applicationFeatureCode,
        this.baseView.mode == "create" ? "create" : "edit",
      );
    },
  },
  methods: {
    goToCreateHuller() {
      this.$router
        .push({
          path: `/huller/create`,
        })
        .then(() => {
          this.recordEditable = true;
        });
    },
    beforeInit() {
      this.recordEditable = true;
    },
    // §9: "Post Sizing" is the create-mode default. The generic
    // SplitViewTemplate wipes every non-special formModel field to undefined
    // when navigating into create mode (templates/split-view.vue), so the
    // default must be (re)applied here rather than relying on the formModel
    // literal alone - matches AlmondcoSampleCode's own recordPostProcess
    // precedent for the same reason. No-op on Review/Edit, where a real
    // SampleTaken value is always already fetched.
    //
    // Bug 77351 fix: also derives SupplierCodeName ("Code - Description")
    // for Review/Edit's read-only Supplier textbox to bind to - the raw
    // SupplierCode has no formatting hook of its own once the field stops
    // being a dxSelectBox (create mode only). SupplierName is already a
    // real, fetched column on this record (api.AlmondcoHullerDetail's own
    // LEFT JOIN, #76532) by the time this runs on a genuine Review/Edit
    // load - this only combines what's already there, no extra round-trip.
    // No-op on create mode (SupplierName isn't set yet; the picker's own
    // displayExpr handles that branch instead - see setFormConfig).
    recordPostProcess(data) {
      if (data.SampleTaken === undefined || data.SampleTaken === null) {
        data.SampleTaken = "Post Sizing";
      }

      if (data.SupplierCode && data.SupplierName) {
        data.SupplierCodeName = data.SupplierCode + " - " + data.SupplierName;
      }

      return data;
    },
    // §6/§9: client-side cascade only, no separate round-trip - populates
    // Supplier Type/Description from the picked Supplier's own record.
    // Guard on e.event (matching grower-pool-splits.vue's identical
    // onDocketPicked precedent) so the framework's own programmatic value
    // resets (create-mode wipe, getRecord) never get treated as a real
    // user pick.
    onSupplierPicked(e) {
      if (!e || !e.event) return;

      if (!e.value) {
        this.formModel.SupplierTypeCode = null;
        this.formModel.SupplierTypeDescription = null;
        return;
      }

      const selectedItem = e.component.option("selectedItem");
      this.formModel.SupplierTypeCode = selectedItem
        ? selectedItem.SupplierTypeCode
        : null;
      this.formModel.SupplierTypeDescription =
        selectedItem && selectedItem.SupplierType
          ? selectedItem.SupplierType.Description
          : null;
    },
    overWritePayload(payload) {
      // Same "untouched field keeps its raw Y/N string, a genuine toggle
      // becomes a real boolean" shape as sample-code.vue's own toYN - the
      // IsSized onInitialized sync above suppresses onValueChanged via
      // beginUpdate()/endUpdate(), so an untouched record's own "Y"/"N"
      // string never converts on its own.
      const toYN = (value) => (value === true || value === "Y" ? "Y" : "N");
      payload.IsSized = toYN(payload.IsSized);

      return payload;
    },
  },
  created() {
    this.beforeInit();
  },
};
</script>
